import { type ComponentType, useEffect, type CSSProperties } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCertainCleanup } from '../../hooks/useCertainCleanup';
import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import { WizardStore } from '../../stores/wizard/WizardStore';

import { clampWizardStep } from './clampWizardStep';
import { StepWrapper } from './StepWrapper';
import type { LocalWizardState, WizardChildProps } from './types';
import styles from './wizard.module.scss';
import { WizardStoreContext } from './WizardStoreContext';

export const Wizard = observer(function Wizard(props: {
  steps: ComponentType<WizardChildProps>[];
  classNameWrapper?: string;
  classNameSteps?: string;
  classNameStepWrapper?: string;
  wrapperStyle?: CSSProperties;
  localWizardState?: LocalWizardState | WizardStore;
  wizardFlowParams?: object;
  redesign?: boolean;
}) {
  const { wizardStore, loyaltyProgramStore } = useStores();

  const wizardState = props.localWizardState || wizardStore;
  const { step, height, setHeight, nextStep, prevStep, forceStep } = wizardState;

  useEffect(() => {
    if (!props.localWizardState && wizardStore.initialStep != null) {
      wizardStore.forceStep(wizardStore.initialStep);
    }
  }, [wizardStore, props.localWizardState]);

  useCertainCleanup(
    // reset step to zero on unmount (only for global wizard store)
    () => {
      if (!props.localWizardState) {
        wizardStore.forceStep(0);
        wizardStore.initialStep = null;
      }
    },
    () => undefined,
    [wizardStore, props.localWizardState],
  );

  // Out-of-bounds renders the nearest real step instead of an empty dialog (see clampWizardStep).
  const renderedStep = clampWizardStep(step, props.steps.length);
  const isOutOfBounds = renderedStep !== step;

  useEffect(() => {
    if (isOutOfBounds) {
      const stepsLength = props.steps.length;
      logToNewRelic('Wizard - step is out of bounds', 'Wizard - step is out of bounds', 'error', {
        step,
        stepsLength,
        isRedesignedWizard: props.redesign,
        isControlledLocally: !!props.localWizardState,
        firstStepCmp: props.steps[0]?.displayName,
        lastStepCmp: props.steps[stepsLength - 1]?.displayName,
      });
      // Resync the store with the step actually rendered, so relative nextStep/prevStep calls
      // work again instead of walking further out of range.
      forceStep(renderedStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutOfBounds]);

  const transform = loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial
    ? 'none'
    : `translateX(-${renderedStep * 100}%)`;

  const wizardStoreForContext = props.localWizardState instanceof WizardStore ? props.localWizardState : wizardStore;

  return (
    <WizardStoreContext.Provider value={wizardStoreForContext}>
      <div className={classNames(styles.wizardWrapper, props.classNameWrapper)} style={props.wrapperStyle}>
        <div className={classNames(styles.wizardSteps, props.classNameSteps)} style={{ transform, height }}>
          {props.steps.map((Component, idx) => {
            const isCurrent = renderedStep === idx;
            return (
              <StepWrapper
                key={idx}
                containerClassName={props.classNameStepWrapper}
                isCurrent={isCurrent}
                redesign={props.redesign}
                setHeight={setHeight}
              >
                <Component
                  forceStep={forceStep}
                  isCurrent={isCurrent}
                  nextStep={nextStep}
                  prevStep={prevStep}
                  wizardFlowParams={props.wizardFlowParams}
                />
              </StepWrapper>
            );
          })}
        </div>
      </div>
    </WizardStoreContext.Provider>
  );
});
